import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * SeasonalWallpaper — a scroll-triggered, full-bleed interlude that switches
 * its imagery + copy based on the current calendar month. The image scales
 * and fades subtly as the user scrolls, revealing an editorial caption.
 *
 * Seasons:
 *  · Feb–May : Orchard bloom  (mango + lychee flowering)
 *  · Jun–Sep : Monsoon        (green, misty, rain-drenched Sal forest)
 *  · Oct–Nov : Forest gold    (autumn Sal turning gold)
 *  · Dec–Jan : Firelight      (winter mornings around the hunting-lodge fire)
 */

const SEASONS = {
  bloom: {
    kicker: "Interlude · In season now",
    title: "Orchard bloom.",
    line:
      "The mango and lychee trees are throwing their first blossoms. Bees, warm afternoons, and a canopy of pale flowers.",
    stamp: "Feb — May",
    image:
      "https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=2400&q=80",
  },
  monsoon: {
    kicker: "Interlude · In season now",
    title: "Monsoon on the estate.",
    line:
      "The Sal forest turns emerald, mist hangs low over the orchard, and the fire place comes back to life on demand.",
    stamp: "Jun — Sep",
    image:
      "https://images.unsplash.com/photo-1503435980610-a51f3ddfee50?auto=format&fit=crop&w=2400&q=80",
  },
  gold: {
    kicker: "Interlude · In season now",
    title: "The forest turns gold.",
    line:
      "By October the private Sal forest is at its most cinematic — long shadows at four, wood-smoke by five, and stars you can count.",
    stamp: "Oct — Nov",
    image:
      "https://images.unsplash.com/photo-1523712999610-f77fbcfc3843?auto=format&fit=crop&w=2400&q=80",
  },
  fire: {
    kicker: "Interlude · In season now",
    title: "Firelight & long evenings.",
    line:
      "Winter mornings begin quiet, misty and golden. The 1930's fireplace in the hunting lodge is lit by 6 pm — and it stays lit.",
    stamp: "Dec — Jan",
    image:
      "https://images.pexels.com/photos/18545165/pexels-photo-18545165.jpeg?auto=compress&w=2400",
  },
};

function seasonForMonth(m /* 1..12 */) {
  if (m >= 2 && m <= 5) return "bloom";
  if (m >= 6 && m <= 9) return "monsoon";
  if (m >= 10 && m <= 11) return "gold";
  return "fire"; // Dec + Jan
}

export default function SeasonalWallpaper() {
  const ref = useRef(null);
  const key = useMemo(() => seasonForMonth(new Date().getMonth() + 1), []);
  const s = SEASONS[key];

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Slow parallax on the image
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1.05, 1.15]);
  const veil = useTransform(scrollYProgress, [0, 0.5, 1], [0.85, 0.55, 0.85]);
  // Copy reveal (opacity + subtle y) — peaks at 50% through
  const copyOpacity = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [0, 1, 0]);
  const copyY = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [28, 0, -28]);

  return (
    <section
      ref={ref}
      data-testid={`season-wallpaper-${key}`}
      className="relative h-[92vh] min-h-[560px] w-full overflow-hidden bg-[#1C1913]"
      aria-label={`Seasonal interlude — ${s.title}`}
    >
      {/* Background image with slow parallax */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -inset-y-[10%] will-change-transform"
      >
        <img
          src={s.image}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>

      {/* Warm dark veil for legibility */}
      <motion.div
        style={{ opacity: veil }}
        className="absolute inset-0 bg-gradient-to-b from-[#1C1913] via-[#1C1913]/40 to-[#1C1913]"
      />
      <div className="absolute inset-0 mix-blend-multiply bg-[#1C1913]/25" />

      {/* Editorial copy */}
      <motion.div
        style={{ opacity: copyOpacity, y: copyY }}
        className="relative z-10 h-full mx-auto max-w-[1600px] px-6 lg:px-12 flex items-end pb-16 md:pb-24"
      >
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono-label text-[#D97645]">{s.kicker}</span>
            <span className="hidden md:inline stamp text-[#F2E7D0]/80">
              {s.stamp}
            </span>
          </div>
          <h2 className="font-display text-[3rem] sm:text-[4.5rem] md:text-[6rem] leading-[0.95] tracking-tighter text-[#F2E7D0]">
            <em className="italic text-[#D97645]">{s.title.split(" ")[0]}</em>{" "}
            {s.title.split(" ").slice(1).join(" ")}
          </h2>
          <p className="mt-6 text-[#F2E7D0]/85 max-w-2xl text-base md:text-lg leading-relaxed">
            {s.line}
          </p>
        </div>
      </motion.div>
    </section>
  );
}
